import stylesheet from './fans-also-like.component.scss?as=stylesheet';
import {html, nothing, LitElement} from 'lit';
import GTM from 'extensions/gtm';
import EventBus from '@mvps-genesis/event-bus';
import RecommendationsService from 'custom-extensions/searchspring-recommendations/services/recommendations-service';
import BeaconService from 'custom-extensions/searchspring-recommendations/services/beacon-service';
import 'custom-extensions/ui-components/x-fans-like-card';

export default class FansAlsoLikeRecommendations extends LitElement {

	static get properties () {
		return {
			_recommendations: {
				type: Array
			},
			list: {
				type: String
			},
			tags: {
				type: String
			},
			type: {
				type: String
			}
		};
	};

	static get styles () {
		return [stylesheet];
	}

	constructor ()
	{
		super();

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);
		this._recommendations = [];
		this.list = 'Product Recommendations';
		this.tags = 'no-results';
		this.type = '';
		this.limits = 2;

		this.addEventListener('fans-also-like-card-click', (e) => {
			this.sendClick(e.detail);
		});
	}

	connectedCallback () {
		super.connectedCallback();

		this.addEventListener('afterFansAlsoLikeRecommendations', () => {
			this.sendImpressions();
			this.initIntersectionObserver();
		});

		// Listen for add to cart event from iframe and handle response
		EventBus.addEventListener('MVPS:addToCartQuickAdd', this.onQuickAddToCart.bind(this), false);
	}

	/**
	 * Invoked when a component is removed from the document's DOM.
	 * @see https://lit.dev/docs/components/lifecycle/#disconnectedcallback
	 * @static
	 */
	disconnectedCallback () {
		EventBus.removeEventListener('MVPS:addToCartQuickAdd', this.onQuickAddToCart.bind(this), false);
		super.disconnectedCallback();
	}

	/**
	 * Updates applicable product details for API calls when a variant has changed.
	 * @param {Event}	e
	 */
	onQuickAddToCart (e) {
		// On successful add to cart
		if (e.detail.success) {
			const productFormClicked = e.detail.form;
			const productCode = productFormClicked.dataset.productCode;
			const productCard = this.shadowRoot.getElementById(`js-fans-also-liked-${productCode}`);
			const productAddedButton = productCard.buttonRef.value;

			productCard.shadowRoot.querySelector('g-modal').close();
			productCard.buttonDisabled = true;
			productAddedButton.classList.add('is-added');
			productAddedButton.innerHTML = '<i class="u-icon-check"></i> Added';
		}
	}

	sendImpressions () {
		if (!this?._recommendations?.length) {
			return;
		}
		const gtmList = this?.list;
		this.gtm.searchSendImpressions(this.gtm.slugify(gtmList), gtmList, this?._recommendations);
		const renderProfile = new BeaconService();
		renderProfile.sendBeacon(this.tags, 'render', this?._recommendations);
	}

	initIntersectionObserver () {
		const options = {
			root: null,
			rootMargin: '0px',
			threshold: 0
		};
		let observer = new IntersectionObserver(this.intersected, options);
		observer.observe(this);
	}

	async getRecommendations () {
		const recommendationsService = new RecommendationsService();
		this._recommendations = await recommendationsService.getRecommendations(this.tags, this.list, this.limits);
	}

	intersected (entries, observer) {
		let fanProducts = [];
		let products = [];

		entries.forEach((entry) => {
			const tags = entry.target.getAttribute('tags');

			if (entry.isIntersecting) {
				fanProducts = entry.target.shadowRoot.querySelectorAll('x-fans-like-card');

				if (fanProducts.length) {
					fanProducts.forEach((product) => {
						const prodAttributesArray = [...product.attributes];
						let productCardAttributes = prodAttributesArray.reduce((productCardAttributes, attribute) => {
							productCardAttributes[attribute.name] = attribute.value;
							return productCardAttributes;
						}, {});
						productCardAttributes.id = productCardAttributes['product-id'];
						products.push(productCardAttributes);
					});
					const impressionProfile = new BeaconService();
					impressionProfile.sendBeacon(tags, 'impression', products);
				}
				// Only trigger on first intersection
				observer.unobserve(entry.target);
			}
		});
	}

	sendClick (data) {
		const clickProfile = new BeaconService();

		clickProfile.sendBeacon(data.item_list_id, 'click', [data]);
	}

	updated () {
		super.updated();

		this.dispatchEvent(new CustomEvent('afterFansAlsoLikeRecommendations'));
	}

	firstUpdated () {
		this.getRecommendations();
	}

	renderProductResults () {
		const gtmData = {
			listId: this.gtm.slugify(this?.list),
			listName: this?.list
		};

		return this._recommendations?.map((product, index) => {
			const pos = index + 1;
			return html`
				<x-fans-like-card
					id = "js-fans-also-liked-${product.code}"
					code = "${product.code}"
					gtm-list = "${gtmData?.listName}"
					gtm-list-id = "${gtmData?.listId}"
					gtm-position = "${pos}"
					image-src-desktop = "${product?.images?.main?.desktop}"
					image-src-mobile = "${product?.images?.main?.mobile}"
					name = "${product.name}"
					original-price = "${product?.customfields?.original_price}"
					price = "${product.price}"
					product-id = "${product?.id}"
					style-number = "${product?.sku}"
					url = "${product.url}"
				></x-fans-like-card>
			`;
		});
	}

	render () {
		if (!this._recommendations?.length) {
			return nothing;
		}
		return html`
			<div class="x-product-fans-also-liked__header x-type-subheading-xsmall">Fans Also Liked</div>
				<div class="x-product-fans-also-liked-products js-gtm-list" data-gtm-list="Fans Also Liked Products">
					${this.renderProductResults()}
				</div>
			</div>
		`;
	}

	// Helpers
	htmlEntities (str) {
		// eslint-disable-next-line newline-per-chained-call
		return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	/**
	 * Slugify a string
	 * @param {String} text
	 * @returns {void}
	 */
	slugify (text) {
		return text.toLowerCase().replace(/[^\w ,-]+/g, '').replace(/ +/g, '-');
	};
};

window.customElements.define('x-ss-fans-also-like', FansAlsoLikeRecommendations);
